// Vegetation around the circuit: coconut-style palms near the track, broadleaf
// rainforest trees, and oil-palm plantation rows further out (what actually
// surrounds Sepang). Leaves are canvas-drawn textures on alpha-tested cards,
// and every species is a thin-instanced mesh, so the whole lot costs a handful
// of draw calls. Each tree gets a soft contact shadow on the ground.
//
// Broadleaf trees are real (low-poly) geometry: trunk, branches and layered
// leaf clumps sharing one texture atlas, so each species is one draw call
// (see vegetationTrees.ts). Every instance gets a slight size and colour shift.

import { Color3, DynamicTexture, Mesh, MeshBuilder, Scene, StandardMaterial, Vector3, VertexData } from './babylon';
import { Matrix, Quaternion } from '@babylonjs/core/Maths/math.vector';
import type { Track } from '../track/track';
import { barrierOffsetAt } from '../track/geometry';
import { blobShadowTexture } from './textures';
import { BROADLEAF_SPECIES, broadleafGeometry, rng } from './vegetationTrees';
import { barkTexture, broadleafAtlas, frondTexture, oilPalmTexture } from './vegetationTextures';

/** Material.MATERIAL_ALPHATEST: cut-out transparency, no sorting needed. */
const ALPHATEST = 1;

export interface Keepout {
  /** Returns true if a tree must not be placed here (buildings, stands). */
  (x: number, z: number): boolean;
}

function cutoutMaterial(scene: Scene, name: string, tex: DynamicTexture, tint = Color3.White()): StandardMaterial {
  tex.hasAlpha = true;
  const m = new StandardMaterial(name, scene);
  m.diffuseTexture = tex;
  m.useAlphaFromDiffuseTexture = true;
  m.transparencyMode = ALPHATEST;
  m.alphaCutOff = 0.45;
  m.backFaceCulling = false;
  m.twoSidedLighting = true;
  m.diffuseColor = tint;
  m.specularColor = new Color3(0.04, 0.05, 0.03);
  // Leaves let light through; keep shaded sides from going black.
  m.emissiveColor = new Color3(0.1, 0.13, 0.07);
  return m;
}

/** Three crossed vertical cards, `width` × `height` metres, base at the origin. */
function crossedCards(scene: Scene, name: string, width: number, height: number, mat: StandardMaterial): Mesh {
  const cards: Mesh[] = [];
  for (let k = 0; k < 3; k++) {
    const c = MeshBuilder.CreatePlane('card', { width, height }, scene);
    c.bakeTransformIntoVertices(Matrix.Translation(0, height / 2, 0).multiply(Matrix.RotationY((k * Math.PI) / 3)));
    cards.push(c);
  }
  const mesh = Mesh.MergeMeshes(cards, true, true)!;
  mesh.name = name;
  mesh.material = mat;
  return mesh;
}

/** A curved frond strip: base at the origin, drooping along +z. */
function frondGeometry(length: number, width: number, droop: number): VertexData {
  const seg = 4;
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  for (let i = 0; i <= seg; i++) {
    const t = i / seg;
    const z = t * length;
    const y = t * length * 0.35 - droop * t * t * length;
    // Slight V-fold across the frond.
    for (const side of [-1, 1]) {
      positions.push((side * width) / 2, y - 0.08 * width, z);
      uvs.push(side < 0 ? 0 : 1, t);
    }
    positions.push(0, y + 0.06 * width, z);
    uvs.push(0.5, t);
  }
  // Two triangles per side per segment: verts per row = 3 (left, right, centre).
  for (let i = 0; i < seg; i++) {
    const a = i * 3;
    const b = (i + 1) * 3;
    indices.push(a, a + 2, b, b, a + 2, b + 2, a + 2, a + 1, b + 2, b + 2, a + 1, b + 1);
  }
  const vd = new VertexData();
  vd.positions = positions;
  vd.uvs = uvs;
  vd.indices = indices;
  const normals: number[] = [];
  VertexData.ComputeNormals(positions, indices, normals);
  vd.normals = normals;
  return vd;
}

/** A palm crown of `count` fronds, merged into one mesh. */
function palmCrown(scene: Scene, name: string, count: number, length: number, droop: number, mat: StandardMaterial): Mesh {
  const parts: Mesh[] = [];
  const rand = rng(count * 7 + Math.round(length * 10));
  for (let i = 0; i < count; i++) {
    const f = new Mesh(`${name}-frond`, scene);
    frondGeometry(length * (0.85 + rand() * 0.25), 1.4, droop * (0.8 + rand() * 0.4)).applyToMesh(f);
    f.rotation.y = (i / count) * Math.PI * 2 + rand() * 0.3;
    // Upper fronds point up, lower ones hang.
    f.rotation.x = -0.35 + (i % 3) * 0.3;
    parts.push(f);
  }
  const crown = Mesh.MergeMeshes(parts, true, true)!;
  crown.name = name;
  crown.material = mat;
  return crown;
}

export interface Placement {
  x: number;
  y: number;
  z: number;
  scale: number;
  yaw: number;
  lean: number;
  /** Extra vertical stretch on top of `scale` (default 1). */
  stretch?: number;
  /** Colour multiplier (default white). */
  tint?: [number, number, number];
}

/** Instance a mesh at placements, with per-instance tints when any placement has one. */
function instance(mesh: Mesh, places: Placement[], scaleY = 1): void {
  const buf = new Float32Array(places.length * 16);
  const q = new Quaternion();
  places.forEach((p, i) => {
    Quaternion.RotationYawPitchRollToRef(p.yaw, p.lean, 0, q);
    const sy = p.scale * scaleY * (p.stretch ?? 1);
    Matrix.Compose(new Vector3(p.scale, sy, p.scale), q, new Vector3(p.x, p.y, p.z)).copyToArray(buf, i * 16);
  });
  mesh.thinInstanceSetBuffer('matrix', buf, 16, true);
  if (places.some((p) => p.tint)) {
    const colors = new Float32Array(places.length * 4);
    places.forEach((p, i) => {
      const [r, g, b] = p.tint ?? [1, 1, 1];
      colors.set([r, g, b, 1], i * 4);
    });
    mesh.thinInstanceSetBuffer('color', colors, 4, true);
  }
  mesh.isPickable = false;
  mesh.alwaysSelectAsActiveMesh = true;
  mesh.freezeWorldMatrix();
}

/**
 * Clearance from the circuit: distance beyond the nearest barrier, using an
 * exact nearest-centreline search so a spot near another part of the track
 * is never mistaken for a safe one.
 */
function clearOfTrack(track: Track, x: number, z: number, margin: number): boolean {
  const p = track.project(x, z);
  return Math.abs(p.offset) > barrierOffsetAt(track, p) + margin;
}

export interface VegetationLayout {
  palms: Placement[];
  oilPalms: Placement[];
  /** One list per entry of BROADLEAF_SPECIES. */
  broadleaf: Placement[][];
  shadows: Placement[];
}

/** Where every tree goes. Deterministic; no Babylon objects, so it can be tested. */
export function scatterVegetation(track: Track, keepout: Keepout, density: number, groundAt: (x: number, z: number) => number): VegetationLayout {
  const out: VegetationLayout = { palms: [], oilPalms: [], broadleaf: BROADLEAF_SPECIES.map(() => []), shadows: [] };
  if (density <= 0) return out;
  const { palms, oilPalms, broadleaf, shadows } = out;
  const rand = rng(77);
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const p of track.points) {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minZ = Math.min(minZ, p.z);
    maxZ = Math.max(maxZ, p.z);
  }
  const ground = groundAt;

  // 1. Palms and broadleaf trees scattered near the circuit, in loose clumps.
  const near = 240;
  for (let x = minX - near; x <= maxX + near; x += 14) {
    for (let z = minZ - near; z <= maxZ + near; z += 14) {
      const clump = 0.5 + 0.5 * Math.sin(x * 0.017 + 0.4) * Math.cos(z * 0.021 - 1.1);
      if (rand() > density * clump * 0.75) continue;
      const px = x + (rand() - 0.5) * 12;
      const pz = z + (rand() - 0.5) * 12;
      if (keepout(px, pz) || !clearOfTrack(track, px, pz, 10)) continue;
      const y = ground(px, pz);
      const isPalm = rand() < 0.55;
      const scale = 0.8 + rand() * 0.5;
      const yaw = rand() * Math.PI * 2;
      if (isPalm) {
        const lean = (rand() - 0.5) * 0.3;
        // Sun-bleached to deep green fronds.
        const k = 0.9 + rand() * 0.2;
        palms.push({ x: px, y, z: pz, scale, yaw, lean, tint: [k * (0.95 + rand() * 0.15), k, k * (0.9 + rand() * 0.1)] });
        continue;
      }
      // Mostly mid-storey trees, some tall emergents, some small ones.
      const pick = rand();
      const v = pick < 0.25 ? 0 : pick < 0.72 ? 1 : 2;
      const s = 0.9 + rand() * 0.42;
      const k = 0.85 + rand() * 0.25;
      const warm = rand();
      broadleaf[v]!.push({
        x: px,
        y: y - 0.2,
        z: pz,
        scale: s,
        yaw,
        lean: 0,
        stretch: 0.9 + rand() * 0.25,
        // Brightness and a little hue shift: olive to blue-green.
        tint: [k * (0.88 + warm * 0.24), k, k * (1.02 - warm * 0.16)],
      });
    }
  }

  // 2. Oil-palm plantation blocks further out (beyond the furthest track point, so
  // no per-tree track check is needed): regular rows with slight jitter.
  const cx = (minX + maxX) / 2;
  const cz = (minZ + maxZ) / 2;
  let reach = 0;
  for (const p of track.points) reach = Math.max(reach, Math.hypot(p.x - cx, p.z - cz));
  const inner = reach + 140;
  const outer = reach + 520;
  for (let x = cx - outer; x <= cx + outer; x += 15) {
    for (let z = cz - outer; z <= cz + outer; z += 13) {
      const r = Math.hypot(x - cx, z - cz);
      if (r < inner || r > outer) continue;
      // Plantations come in blocks separated by roads and clearings.
      if (Math.sin(x * 0.006) * Math.cos(z * 0.007) < -0.35) continue;
      if (rand() > density) continue;
      const px = x + (rand() - 0.5) * 2;
      const pz = z + (rand() - 0.5) * 2;
      if (keepout(px, pz)) continue;
      oilPalms.push({ x: px, y: ground(px, pz), z: pz, scale: 0.85 + rand() * 0.35, yaw: rand() * Math.PI * 2, lean: 0 });
    }
  }
  // Keep the near-track palms within a phone-friendly budget.
  while (palms.length > 900) palms.splice(Math.floor(rand() * palms.length), 1);
  const broadleafCount = broadleaf.flat().length;
  for (const list of broadleaf) {
    const cap = Math.floor((1200 * list.length) / Math.max(1200, broadleafCount));
    while (list.length > cap) list.splice(Math.floor(rand() * list.length), 1);
  }
  // Contact shadows under the near trees that made the cut.
  for (const p of palms) shadows.push({ x: p.x, y: p.y + 0.05, z: p.z, scale: 5, yaw: p.yaw, lean: 0 });
  broadleaf.forEach((list, v) => {
    for (const p of list) shadows.push({ x: p.x, y: p.y + 0.25, z: p.z, scale: BROADLEAF_SPECIES[v]!.crownRadius * 2.2 * p.scale, yaw: p.yaw, lean: 0 });
  });
  return out;
}

export function buildVegetation(scene: Scene, track: Track, keepout: Keepout, density: number, groundAt: (x: number, z: number) => number): Mesh[] {
  if (density <= 0) return [];
  const { palms, oilPalms, broadleaf, shadows } = scatterVegetation(track, keepout, density, groundAt);

  // Materials are only made for meshes that exist: the caller disposes
  // scenery through its meshes' materials when the quality level changes.
  const meshes: Mesh[] = [];

  // Coconut-style palm: tall slender trunk, crown of long fronds.
  if (palms.length) {
    const frondMat = cutoutMaterial(scene, 'frond-mat', frondTexture(scene));
    const barkMat = new StandardMaterial('bark-mat', scene);
    barkMat.diffuseTexture = barkTexture(scene);
    barkMat.specularColor = Color3.Black();
    const trunk = MeshBuilder.CreateCylinder('palm-trunk', { height: 1, diameterTop: 0.3, diameterBottom: 0.45, tessellation: 7 }, scene);
    trunk.bakeTransformIntoVertices(Matrix.Translation(0, 0.5, 0));
    trunk.material = barkMat;
    instance(trunk, palms.map((p) => ({ ...p, tint: undefined })), 11);
    meshes.push(trunk);
    const crown = palmCrown(scene, 'palm-crown', 9, 4.6, 0.55, frondMat);
    // Crowns sit on top of the (leaning) trunks.
    instance(
      crown,
      palms.map((p) => {
        const top = Vector3.TransformCoordinates(new Vector3(0, 11 * p.scale, 0), Matrix.RotationYawPitchRoll(p.yaw, p.lean, 0));
        return { ...p, x: p.x + top.x, y: p.y + top.y, z: p.z + top.z, lean: 0 };
      }),
    );
    meshes.push(crown);
  }

  // Oil palms: crossed cards (6 triangles each), fine at plantation distance.
  if (oilPalms.length) {
    const oil = crossedCards(scene, 'oil-palm', 11, 9, cutoutMaterial(scene, 'oil-palm-mat', oilPalmTexture(scene)));
    instance(oil, oilPalms);
    meshes.push(oil);
  }

  // Broadleaf: one mesh (trunk, branches and leaf clumps) per species.
  let leafMat: StandardMaterial | undefined;
  BROADLEAF_SPECIES.forEach((sp, v) => {
    const places = broadleaf[v]!;
    if (!places.length) return;
    if (!leafMat) {
      leafMat = cutoutMaterial(scene, 'broadleaf-mat', broadleafAtlas(scene));
      // Card normals are authored to point out of the crown; don't flip them on back faces.
      leafMat.twoSidedLighting = false;
      leafMat.alphaCutOff = 0.4;
      leafMat.emissiveColor = new Color3(0.13, 0.15, 0.1);
    }
    const a = broadleafGeometry(sp);
    const tree = new Mesh(`broadleaf-${v}`, scene);
    const vd = new VertexData();
    vd.positions = a.positions;
    vd.normals = a.normals;
    vd.uvs = a.uvs;
    vd.colors = a.colors;
    vd.indices = a.indices;
    vd.applyToMesh(tree);
    tree.material = leafMat;
    instance(tree, places);
    meshes.push(tree);
  });

  // Soft contact shadows under the near trees.
  if (shadows.length) {
    const shadow = MeshBuilder.CreateGround('tree-shadow', { width: 1, height: 1 }, scene);
    const shadowMat = new StandardMaterial('tree-shadow-mat', scene);
    shadowMat.diffuseColor = Color3.Black();
    shadowMat.specularColor = Color3.Black();
    shadowMat.opacityTexture = blobShadowTexture(scene);
    shadowMat.disableLighting = true;
    shadowMat.alpha = 0.55;
    shadow.material = shadowMat;
    instance(shadow, shadows);
    meshes.push(shadow);
  }
  return meshes;
}
